<template>
  <div class="tender-container-wrapper">
    <div class="tender-container clearfix">
      <h4>
        <HKLLeftRight>
          <template #left>
            <span>{{ $t('tender.dashboardTableTitle') }}</span>
            <i
              class="fa fa-refresh dark flat icon_refresh"
              @click="loadTenderTable()"
            />
          </template>
          <template
            #right
            v-if="isHKlandUser"
          >
            <HKLButton
              class="btn-reset"
              action="resetFilter"
              color="darkblue"
              @click="() => resetFilterObject()"
            />
          </template>
        </HKLLeftRight>
      </h4>
      <div class="tender-table">
        <table class="hover">
          <thead>
            <tr>
              <th
                v-for="header in itemHeaders"
                :key="header.code"
                :class="header.class"
                @click="sortBy(header.code)"
              >
                {{ header.name() }} {{ orderbyAttr === header.code ? (reverse ? '&#9660;' : '&#9650;') : null }}
              </th>
            </tr>
            <tr v-if="isHKlandUser">
              <th
                v-for="header in itemHeaders"
                :key="header.code"
              >
                <component
                  :is="header.filterComponent"
                  v-model="filterObject[header.code]"
                  v-bind="header.filterComponentAttrs || {}"
                />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in orderedItems"
              :key="item.id"
            >
              <td>
                <a
                  class="link_text"
                  :class="{'no-finger': disableLink(item)}"
                  :disabled="disableLink(item)"
                  :title="item.rejectReason"
                  @click="navToPage(item)"
                >
                  {{ item.prNumber }}
                </a>
              </td>
              <td>
                <a
                  class="link_text"
                  :class="{'no-finger': disableLink(item)}"
                  :disabled="disableLink(item)"
                  :title="item.rejectReason"
                  @click="navToPage(item)"
                >
                  {{ item.prDescription }}
                </a>
              </td>
              <td>{{ item.site }}</td>
              <td class="number_text">
                <span v-if="item.showQIndicator">(Q) </span>{{ item.statusText }}
              </td>
              <td class="number_text">
                {{ dateUtil.formatDate(item.updateDate, true, true) }}
              </td>
              <td :class="{ 'number_text': true, 'closing-date-red': item.isRepresentative}">
                <span>{{ item.closingDate && dateUtil.formatDate(item.closingDate, true, true) || '--' }}</span>
              </td>
              <td class="number_text">
                {{ item.budget ? currencyFormatter.format(item.budget || 0, 'USD') : '--' }}
              </td>
              <td class="number_text">
                {{ item.currencyCode }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="nav_line">
        <ConfirmCreate
          v-if="showCreateButton()"
          class="nav_button"
        />
        <button
          v-if="showViewAllButton()"
          type="button"
          class="nav_button btn btn--darkblue"
          @click="navToAllTenders()"
        >
          {{ $t('action.viewAllTenders') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, unref, onMounted, ref } from 'vue-demi'
import lodash from 'lodash'
import currencyFormatter from 'currency-formatter'
import ConfirmCreate from '@/modules/tenders/ConfirmCreate'
import dateUtil from '@/utils/dateUtil'
import { aclHelper, aclPermsType } from '@/utils/aclHelper'
import { formatReviewTender } from './utils'
import { getReviewList } from '@/api/tenders/request'
import { mapGetters, mapActions } from 'vuex'
import { navToDetailPage } from '@/utils/navigation'
import { tenderStatus as TENDER_STATUS } from '@/utils/locale'
import HKLInput from '@/controls/Input/HKLInput.vue'
import useRegionRef from '@/utils/useRegionRef'
import i18n from '@/utils/i18n'
import HKLInputOperatorNumber from '@/controls/Input/HKLInputOperatorNumber.vue'
import DatePicker from '@/controls/DatePicker.vue'
import numberUtils from '@/utils/numberUtils'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import { useStoreGetters } from '@/store/index'
import { ADDENDUM_STATUS } from '@/utils/consts'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight.vue'
import HKLButton from '@/controls/Button/HKLButton.vue'
import { filterValueFn } from '../utils'

const cachedFilterObject = useRegionRef({}, { logoutReset: true })

export default {
  name: 'TendersTable',
  components: {
    ConfirmCreate,
    HKLLeftRight,
    HKLButton,
  },
  props: {
    model: {
      type: Array,
      default: undefined,
    },
  },
  setup () {
    const { pr: prCurrencies } = useStoreGetters('currencies', ['pr'])
    const itemHeaders = computed(() => [
      {
        code: 'prNumber',
        name: () => i18n.t('tender.award.placeholder.number'),
        class: 'number-header',
      },
      {
        code: 'prDescription',
        name: () => i18n.t('name'),
        class: 'name-header',
        filterNoCase: true,
      },
      {
        code: 'site',
        name: () => i18n.t('tender.general.siteId'),
        class: 'site-header',
        filterNoCase: true,
      },
      {
        code: 'statusText',
        name: () => i18n.t('status.name'),
        class: 'number_text status-header',
        filterComponent: HKLMultiSelect,
        filterComponentDefaultValue: [],
        filterComponentAttrs: {
          includeAll: true,
          showSearch: true,
          options: lodash.pullAll([
            ...Object.keys(TENDER_STATUS),
            `(Q) ${TENDER_STATUS.REVIEW}`,
            ...Object.keys(ADDENDUM_STATUS).map(rec => `A-${rec}`),
          ], [TENDER_STATUS.DECLINED, TENDER_STATUS.AWARDED, TENDER_STATUS.REGRETTED, TENDER_STATUS.CANCELLED]),
        },
        onFilterFn: (value, filterValue, _, item) => {
          const realValue = value === TENDER_STATUS.REVIEW && item.showQIndicator ? `(Q) ${TENDER_STATUS.REVIEW}` : value
          return filterValueFn(realValue, filterValue)
        },
      },
      {
        code: 'updateDate',
        name: () => i18n.t('lastModified'),
        class: 'number_text date-header',
        filterComponent: DatePicker,
        filterComponentAttrs: {
          calendarButton: true,
          calendarButtonIcon: 'fa fa-calendar',
          canPutTotop: false,
        },
        onFilterFn: (value, filterValue) => {
          const targetDate = dateUtil.getStartOfDay(value)
          const filterDate = dateUtil.getStartOfDay(filterValue)
          return targetDate === filterDate
        },
      },
      {
        code: 'closingDate',
        name: () => i18n.t('tender.closingDate'),
        class: 'number_text date-header',
        filterComponent: DatePicker,
        filterComponentAttrs: {
          calendarButton: true,
          calendarButtonIcon: 'fa fa-calendar',
          canPutTotop: false,
        },
        onFilterFn: (value, filterValue) => {
          const targetDate = dateUtil.getStartOfDay(value)
          const filterDate = dateUtil.getStartOfDay(filterValue)
          return targetDate === filterDate
        },
      },
      {
        code: 'budget',
        name: () => i18n.t('amount'),
        class: 'number_text amount-header',
        filterComponent: HKLInputOperatorNumber,
        onFilterFn: (value, filterValue) => {
          return numberUtils.compareNumberFree(value, filterValue)
        },
      },
      {
        code: 'currencyCode',
        name: () => i18n.t('currency'),
        class: 'number_text amount-header',
        filterComponent: HKLMultiSelect,
        filterComponentDefaultValue: [],
        filterComponentAttrs: {
          showSearch: true,
          options: unref(prCurrencies).sort(),
        },
      },
    ].map((item) => Object.assign({ filterComponent: HKLInput }, item)))
    const initFilterObject = () => {
      return unref(itemHeaders).reduce((obj, { code, filterComponentDefaultValue }) => {
        obj[code] = filterComponentDefaultValue || ''
        return obj
      }, {})
    }
    onMounted(() => {
      if (Object.keys(cachedFilterObject.value).length > 0) return
      cachedFilterObject.value = initFilterObject()
    })

    const orderbyAttr = ref('')
    const reverse = ref(0)
    const items = ref([])
    const filteredItems = computed(() => {
      return unref(items).filter((item, index) => {
        return unref(itemHeaders).every(({ code, onFilterFn, filterNoCase = false }) => {
          const filterValue = unref(cachedFilterObject)[code]
          const value = item[code]
          const result = filterValueFn(value, filterValue, {
            noCase: filterNoCase,
            customDiffFn: typeof onFilterFn === 'function' ? (a, b) => onFilterFn(a, b, index, item) : undefined,
          })
          return result
        })
      })
    })
    return {
      items,
      filteredItems,
      orderbyAttr,
      reverse,
      itemHeaders,
      filterObject: cachedFilterObject,
      resetFilterObject () {
        cachedFilterObject.value = initFilterObject()
        orderbyAttr.value = ''
        reverse.value = 0
      },
    }
  },
  data () {
    return {
      currencyFormatter,
      groupOrderBy: 'closingDate',
      dateUtil,
    }
  },
  computed: {
    ...mapGetters('tenders', ['allTenders']),
    ...mapGetters('user', ['currentUser', 'isHKlandUser']),
    isTenderAccess () {
      return aclHelper.hasTendersAccess()
    },
    isTenderRep () {
      return aclHelper.hasTenderRepAccess()
    },
    hasTenderApproveAccess () {
      return aclHelper.getAccess(aclPermsType.tenders.approve)
    },
    orderedItems () {
      if (this.orderbyAttr) {
        const result = lodash.orderBy(this.filteredItems, this.orderbyAttr)
        return this.reverse ? result.reverse() : result
      }
      return this.filteredItems
    },
  },
  watch: {
    items () {
      this.$emit('update:model', this.items)
    },
  },
  async mounted () {
    await this.loadTenderTable()
  },
  methods: {
    ...mapActions('tenders', [
      'loadAllTenders',
    ]),
    navToAllTenders () {
      this.$router.push({
        name: 'tenders',
      })
    },
    navToPage (item) {
      navToDetailPage(item)
    },
    disableLink (item) {
      return !item.hasAccess
    },
    async loadTenderTable () {
      this.items = []

      if (this.isTenderAccess) {
        await this.loadAllTenders()
        const tenderFilteredList = Object.values(this.allTenders).filter(this.filterTenders).map(formatReviewTender)
        this.items.push(...lodash.orderBy(tenderFilteredList, this.groupOrderBy))
      }

      if (this.isTenderRep) {
        const reviewList = await getReviewList()
        const formatedTenders = reviewList.filter(this.filtertForTenderRep).map(formatReviewTender)
        this.items.push(...lodash.orderBy(formatedTenders, this.groupOrderBy))
      }
    },
    filtertForTenderRep (item) {
      return !(item.general.status === TENDER_STATUS.WAPPR && item.general.approvedBy === this.currentUser.id)
    },
    filterTenders (record) {
      if (this.hasTenderApproveAccess) {
        const userId = this.currentUser.id
        return record.general.managedBy === userId ||
          (record.general.approvedBy === userId &&
          record.general.status === TENDER_STATUS.WAPPR)
      }
      return true
    },

    sortBy (attr) {
      if (this.orderbyAttr === attr) {
        this.reverse = !this.reverse
      }
      this.orderbyAttr = attr
    },
    showCreateButton () {
      return aclHelper.getAccess(aclPermsType.tenders.createUpdate)
    },
    showViewAllButton () {
      return aclHelper.hasTendersAccess()
    },
  },
}
</script>

<style scoped src="styles/dashboard/datatable.scss"></style>
<style lang="scss" scoped>
.number-header {
  width: 10%
}
.name-header {
  width: 40%
}
.status-header {
  min-width: 160px;
  width: 13%
}
.site-header {
  min-width: 100px;
  width: 7%
}
.date-header {
  min-width: 157px;
  width: 10%
}
.amount-header {
  min-width: 125px;
  width: 10%
}
a[disabled] {
  text-decoration: none;
  color: #0C3A58;
}
.no-finger {
  cursor: default;
}

th > .vdp-datepicker {
  width: 100%;
}

.tender-table thead tr:nth-child(2) th {
  vertical-align: top;

  > .hkl-input-box,
  > .vdp-datepicker,
  > .hkl-select {
    display: block;
    min-height: 40px;
  }

  ::v-deep .ant-select-selection {
    min-height: 40px;
  }
}

h4 {
  &:has(.btn-reset) {
    padding-bottom: 8px;
  }
  & > .hkl-left-right {
    align-items: center;
    span + .icon_refresh {
      margin-left: 5px;
    }
  }
}
</style>
